/**
 * Hooks de asignaciones de tareas (owner / assignee / watcher).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { TaskAssignmentsInsert } from '@/types/database.types';

import * as assignmentsApi from '../api/taskAssignments';
import { syncTaskToCalendar } from '../api/tasks';
import { TASK_KEYS } from './keys';

export function useTaskAssignments(taskId: string) {
  return useQuery({
    queryKey: TASK_KEYS.assignments(taskId),
    queryFn: () => assignmentsApi.listAssignmentsForTask(taskId),
    enabled: !!taskId,
  });
}

export function useAddAssignment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaskAssignmentsInsert) => assignmentsApi.addAssignment(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TASK_KEYS.assignments(taskId) });
      qc.invalidateQueries({ queryKey: TASK_KEYS.activity(taskId) });
      // Re-sincronizar el evento: el nuevo asignado entra como invitado en Google.
      syncTaskToCalendar(taskId);
      toast.success('Asignación agregada');
    },
    onError: (error: Error) =>
      toast.error('No se pudo asignar', { description: error.message }),
  });
}

export function useRemoveAssignment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      assignmentsApi.removeAssignment(taskId, userId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TASK_KEYS.assignments(taskId) });
      qc.invalidateQueries({ queryKey: TASK_KEYS.activity(taskId) });
      // Re-sincronizar el evento: quitar el asignado lo saca de invitados en Google.
      syncTaskToCalendar(taskId);
      toast.success('Asignación quitada');
    },
    onError: (error: Error) =>
      toast.error('No se pudo quitar', { description: error.message }),
  });
}
